import { createContext, useEffect, useState } from 'react'

import { useParams } from 'react-router-dom'

import {
  IProductDetails,
  IProductItem,
} from '../../sdk/products/products.types'
import {
  IItemShortTreeTraceability,
  IItemTraceability,
} from '../../sdk/traceability/traceability.types'
import { sdk } from '../../services/sdk'
import { IPaginatedResponse } from '../../types/paginated-response.type'

interface ProductDetailsContext {
  productDetails?: IProductDetails
  productItems?: IPaginatedResponse<IProductItem[]>
  selectedItem?: IProductItem
  itemTraceability?: IItemTraceability[]
  itemBlockchainTree?: IItemShortTreeTraceability
  openAddItemModal: boolean
  actions: {
    getDetails: () => Promise<void>
    getItems: (
      search?: string,
      startDate?: Date,
      endDate?: Date,
    ) => Promise<void>
    setSelectedItem: (item?: IProductItem) => void
    getItemTraceability: (itemId: string) => Promise<void>
    getItemBlockchainTree: (itemId: string) => Promise<void>
    setOpenAddItemModal: (open: boolean) => void
  }
}

interface ProductDetailsProviderProps {
  children: React.ReactNode
}

export const ProductDetailsContext = createContext({} as ProductDetailsContext)

export function ProductDetailsProvider(props: ProductDetailsProviderProps) {
  const [openAddItemModal, setOpenAddItemModal] = useState(false)
  const [productDetails, setProductDetails] = useState<IProductDetails>()
  const [productItems, setProductItems] =
    useState<IPaginatedResponse<IProductItem[]>>()

  const [selectedItem, setSelectedItem] = useState<IProductItem>()
  const [itemTraceability, setItemTraceability] =
    useState<IItemTraceability[]>()
  const [itemBlockchainTree, setItemBlockchainTree] =
    useState<IItemShortTreeTraceability>()

  const { code } = useParams()

  const getDetails = async () => {
    const response = await sdk.products.getDetails(code ?? '')

    setProductDetails(response)
  }

  const getItems = async (
    search?: string,
    startDate?: Date,
    endDate?: Date,
  ) => {
    const response = await sdk.products.getItems(
      code ?? '',
      search,
      startDate,
      endDate,
    )
    setProductItems(response)
  }

  const getItemTraceability = async (itemId: string) => {
    const response = await sdk.traceability.item(itemId)
    setItemTraceability(response)
  }

  const getItemBlockchainTree = async (itemId: string) => {
    const response = await sdk.traceability.blockchainTreeShort(itemId)
    setItemBlockchainTree(response)
  }

  useEffect(() => {
    if (selectedItem?.id) {
      setItemTraceability(undefined)
      setItemBlockchainTree(undefined)

      getItemTraceability(selectedItem?.id)
      getItemBlockchainTree(selectedItem?.id)
    }
  }, [selectedItem?.id])

  return (
    <ProductDetailsContext.Provider
      value={{
        productDetails,
        productItems,
        selectedItem,
        itemTraceability,
        itemBlockchainTree,
        openAddItemModal,
        actions: {
          getDetails,
          getItems,
          setSelectedItem,
          getItemTraceability,
          getItemBlockchainTree,
          setOpenAddItemModal,
        },
      }}
    >
      {props.children}
    </ProductDetailsContext.Provider>
  )
}
